import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bridge } from './lib/bridge';
import { api } from './lib/api';
import { AppShell } from './components/AppShell';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { Home } from './pages/Home';
import { Calls } from './pages/Calls';
import { CallDetail } from './pages/CallDetail';
import { Sms } from './pages/Sms';
import { Todos } from './pages/Todos';
import { More } from './pages/More';
import { BlockedContacts } from './pages/BlockedContacts';
import { Chat } from './pages/Chat';

function RequireAuth({ children }: { children?: React.ReactNode }) {
  const token = bridge().getAuthToken();
  if (!token) return <Navigate to="/login" replace />;
  return children ? <>{children}</> : <Outlet />;
}

/**
 * P11 — 메인 진입 가드.
 *  - 미 인증 → /login
 *  - 인증됐는데 onboarded_at null → /signup (강제 진입)
 *  - 그 외엔 AppShell
 */
function RequireOnboarded() {
  const token = bridge().getAuthToken();
  const { data, isPending, fetchStatus } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    enabled: !!token,
    retry: false,
    staleTime: 60_000,
  });
  if (!token) return <Navigate to="/login" replace />;
  // 첫 응답을 실제로 기다리는 동안에만 스피너. 서버 도달 실패(fetchStatus idle + 에러)면
  // 막지 않고 진입 — privacy-first: 로컬(폰 Room) 데이터는 서버 없이도 봐야 함.
  if (isPending && fetchStatus === 'fetching') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[12px] text-[var(--text-muted)]">
        Butler를 준비하는 중…
      </div>
    );
  }
  if (data && 'user_id' in data) {
    if (!data.onboarded_at) return <Navigate to="/signup" replace />;
  }
  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    path: '/signup',
    element: (
      <RequireAuth>
        <Signup />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: <RequireOnboarded />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Home /> },
          { path: 'calls', element: <Calls /> },
          { path: 'calls/:id', element: <CallDetail /> },
          { path: 'sms', element: <Sms /> },
          { path: 'todos', element: <Todos /> },
          { path: 'chat', element: <Chat /> },
          { path: 'more', element: <More /> },
          { path: 'more/blocked', element: <BlockedContacts /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
